import { createApi } from '@reduxjs/toolkit/query/react';
import {
  ContractorCredentialingSummary,
  ContractorOwner,
  ContractorPayerCompliance,
  SaveContractorOwnerRequest,
} from '@src/types/contractor';
import {
  ComplianceLogEntry,
  CredentialRecord,
  NoteAuditEntry,
  PersonnelNote,
  PersonnelNoteInput,
  SaveCredentialRecordRequest,
} from '@src/types/personnel';
import { baseQuerySample } from '@store/helpers';
import { ContractOptions, contractQueryFn } from './credentialingContract';
import { contractorCredentialingMocks } from './mocks/contractorCredentialingMocks';

// TODO: none of the contractor credentialing endpoints exist yet, the paths
// follow the Personnel and Vehicles ones; until they do the tabs show no data
// the backend has none of these endpoints yet, so every one answers from the
// mock; drop alwaysMock here once it does and VITE_CREDENTIALING_MOCKS decides
const mockedQueryFn = <Arg, Result>(
  request: Parameters<typeof contractQueryFn<Arg, Result>>[0],
  mock: (arg: Arg) => Result,
  options: ContractOptions<Arg, Result> = {}
) => contractQueryFn(request, mock, { ...options, alwaysMock: true });

const BASE = '/v1/contractors/credentialing';

type SaveInsuranceRequest = Pick<
  SaveCredentialRecordRequest,
  'id' | 'newFiles' | 'phoneFiles' | 'removedFileIds' | 'type' | 'values'
>;

type CreateContractorNoteRequest = { note: PersonnelNoteInput };
type UpdateContractorNoteRequest = { noteId: number; text: string };
type ContractorNoteAuditRequest = { noteId: number };

const insuranceFormData = ({
  newFiles,
  phoneFiles = [],
  removedFileIds,
  type,
  values,
}: SaveInsuranceRequest) => {
  const body = new FormData();
  body.append('type', type);
  body.append('values', JSON.stringify(values));
  body.append('removedFileIds', JSON.stringify(removedFileIds));
  body.append('phoneFileIds', JSON.stringify(phoneFiles.map(({ id }) => id)));
  newFiles.forEach(({ file }) => body.append('files', file));
  return body;
};

export const contractorCredentialingApi = createApi({
  reducerPath: 'ContractorCredentialing',
  tagTypes: [
    'ContractorComplianceLog',
    'ContractorInsurance',
    'ContractorNotes',
    'ContractorOwners',
    'ContractorPayerCompliance',
    'ContractorSummary',
  ],
  baseQuery: baseQuerySample,
  endpoints: (builder) => ({
    getContractorCredentialingSummary: builder.query<
      ContractorCredentialingSummary | null,
      void
    >({
      queryFn: mockedQueryFn(
        () => ({ url: `${BASE}/summary` }),
        contractorCredentialingMocks.summary,
        { whenMissing: null }
      ),
      providesTags: ['ContractorSummary'],
    }),
    getContractorInsurance: builder.query<Array<CredentialRecord>, void>({
      queryFn: mockedQueryFn(
        () => ({ url: `${BASE}/insurance` }),
        contractorCredentialingMocks.insurance,
        { whenMissing: [] }
      ),
      providesTags: ['ContractorInsurance'],
    }),
    saveContractorInsurance: builder.mutation<
      CredentialRecord,
      SaveInsuranceRequest
    >({
      queryFn: mockedQueryFn(
        (request: SaveInsuranceRequest) => ({
          url: `${BASE}/insurance/${request.id ? `update/${request.id}` : 'create'}`,
          method: request.id ? 'PUT' : 'POST',
          body: insuranceFormData(request),
        }),
        contractorCredentialingMocks.saveInsurance
      ),
      invalidatesTags: (_result, error) =>
        error
          ? []
          : [
              'ContractorComplianceLog',
              'ContractorInsurance',
              'ContractorPayerCompliance',
              'ContractorSummary',
            ],
    }),
    getContractorOwners: builder.query<Array<ContractorOwner>, void>({
      queryFn: mockedQueryFn(
        () => ({ url: `${BASE}/owners` }),
        contractorCredentialingMocks.owners,
        { whenMissing: [] }
      ),
      providesTags: ['ContractorOwners'],
    }),
    saveContractorOwner: builder.mutation<
      ContractorOwner,
      SaveContractorOwnerRequest
    >({
      queryFn: mockedQueryFn(
        ({ id, note, owner }: SaveContractorOwnerRequest) => ({
          url: `${BASE}/owners/${id ? `update/${id}` : 'create'}`,
          method: id ? 'PUT' : 'POST',
          body: id ? { ...owner, note } : owner,
        }),
        contractorCredentialingMocks.saveOwner
      ),
      // an edit's note is a contractor note too
      invalidatesTags: (_result, error) =>
        error ? [] : ['ContractorOwners', 'ContractorNotes'],
    }),
    getContractorPayerCompliance: builder.query<
      Array<ContractorPayerCompliance>,
      void
    >({
      queryFn: mockedQueryFn(
        () => ({ url: `${BASE}/payer-compliance` }),
        contractorCredentialingMocks.payerCompliance,
        { whenMissing: [] }
      ),
      providesTags: ['ContractorPayerCompliance'],
    }),
    getContractorComplianceLog: builder.query<Array<ComplianceLogEntry>, void>({
      queryFn: mockedQueryFn(
        () => ({ url: `${BASE}/compliance-log` }),
        contractorCredentialingMocks.complianceLog,
        { whenMissing: [] }
      ),
      providesTags: ['ContractorComplianceLog'],
    }),
    getContractorNotes: builder.query<Array<PersonnelNote>, void>({
      queryFn: mockedQueryFn(
        () => ({ url: `${BASE}/notes` }),
        contractorCredentialingMocks.notes,
        { whenMissing: [] }
      ),
      providesTags: ['ContractorNotes'],
    }),
    createContractorNote: builder.mutation<
      PersonnelNote,
      CreateContractorNoteRequest
    >({
      queryFn: mockedQueryFn(
        ({ note }: CreateContractorNoteRequest) => ({
          url: `${BASE}/notes/create`,
          method: 'POST',
          body: note,
        }),
        contractorCredentialingMocks.createNote
      ),
      invalidatesTags: (_result, error) => (error ? [] : ['ContractorNotes']),
    }),
    updateContractorNote: builder.mutation<
      PersonnelNote,
      UpdateContractorNoteRequest
    >({
      queryFn: mockedQueryFn(
        ({ noteId, text }: UpdateContractorNoteRequest) => ({
          url: `${BASE}/notes/update/${noteId}`,
          method: 'PUT',
          body: { text },
        }),
        contractorCredentialingMocks.updateNote
      ),
      invalidatesTags: (_result, error) => (error ? [] : ['ContractorNotes']),
    }),
    getContractorNoteAudit: builder.query<
      Array<NoteAuditEntry>,
      ContractorNoteAuditRequest
    >({
      queryFn: mockedQueryFn(
        ({ noteId }: ContractorNoteAuditRequest) => ({
          url: `${BASE}/notes/${noteId}/audit`,
        }),
        contractorCredentialingMocks.noteAudit
      ),
    }),
  }),
});

export const {
  useCreateContractorNoteMutation,
  useGetContractorComplianceLogQuery,
  useGetContractorCredentialingSummaryQuery,
  useGetContractorInsuranceQuery,
  useGetContractorNotesQuery,
  useGetContractorOwnersQuery,
  useGetContractorPayerComplianceQuery,
  useLazyGetContractorNoteAuditQuery,
  useSaveContractorInsuranceMutation,
  useSaveContractorOwnerMutation,
  useUpdateContractorNoteMutation,
} = contractorCredentialingApi;
